/* ==========================================================================
   Ambury search overlay.

   Presentation and lifecycle only. The search itself is Dawn's
   <predictive-search> element; this file just opens the panel and loads
   Dawn's search scripts on FIRST INTERACTION with a search trigger, so no
   page pays for them until someone actually searches.

   Load order matters: predictive-search.js declares
   `class PredictiveSearch extends SearchForm`, so search-form.js must be
   evaluated first or the class definition throws.

   Focus trapping reuses global.js's trapFocus/removeTrapFocus so behaviour
   matches the cart drawer and the menu drawer.
   ========================================================================== */
(function () {
  'use strict';

  var overlay = null;
  var panel = null;
  var input = null;
  var emptyState = null;
  var lastTrigger = null;
  var assetsPromise = null;

  function root() {
    if (!overlay) overlay = document.querySelector('[data-ambury-search]');
    return overlay;
  }

  /* --- scrollbar compensation (also set by the cart drawer script) -------- */
  function setScrollbarWidth() {
    var w = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--ambury-sbw', (w > 0 ? w : 0) + 'px');
  }

  /* --- lazy asset loading ------------------------------------------------- */
  /* True once the custom element exists, i.e. the script has already run. */
  function defined(tagName) {
    return !!(window.customElements && window.customElements.get(tagName));
  }

  /* True if any <script> on the page already points at this file, whatever its
     asset hash or query string. */
  function alreadyOnPage(src) {
    if (!src) return true;
    var file = src.split('?')[0].split('/').pop();
    if (!file) return true;
    var tags = document.querySelectorAll('script[src]');
    for (var i = 0; i < tags.length; i++) {
      if (tags[i].getAttribute('src').indexOf(file) !== -1) return true;
    }
    return false;
  }

  /* Re-evaluating a file that declares a class throws
     "Identifier 'SearchForm' has already been declared" and kills the rest of
     the handler. theme.liquid already ships search-form.js on every page, so
     both guards below matter. */
  function loadScript(src, tagName) {
    return new Promise(function (resolve, reject) {
      if (!src) return resolve();
      if (tagName && defined(tagName)) return resolve();
      if (alreadyOnPage(src)) return resolve();

      var el = document.createElement('script');
      el.src = src;
      el.defer = true;
      el.setAttribute('data-ambury-src', src);
      el.onload = resolve;
      el.onerror = reject;
      document.head.appendChild(el);
    });
  }

  function loadStyle(href) {
    if (document.querySelector('link[data-ambury-href="' + href + '"]')) return;
    var el = document.createElement('link');
    el.rel = 'stylesheet';
    el.href = href;
    el.setAttribute('data-ambury-href', href);
    document.head.appendChild(el);
  }

  function loadSearchAssets() {
    if (assetsPromise) return assetsPromise;

    var o = root();
    if (!o) return Promise.resolve();

    var css = o.getAttribute('data-asset-predictive-css');
    var formSrc = o.getAttribute('data-asset-search-form');
    var predSrc = o.getAttribute('data-asset-predictive');

    if (css) loadStyle(css);

    /* search-form.js first: predictive-search.js extends SearchForm. Both calls
       no-op when the element is already defined or the file is already on the
       page, so nothing is evaluated twice. */
    assetsPromise = loadScript(formSrc, 'search-form')
      .then(function () { return loadScript(predSrc, 'predictive-search'); })
      .catch(function () {
        /* If the scripts fail, the form is still a real GET to /search. */
        assetsPromise = null;
      });

    return assetsPromise;
  }

  /* --- empty state -------------------------------------------------------- */
  function syncEmptyState() {
    if (!emptyState || !input) return;
    emptyState.hidden = input.value.trim().length > 0;
  }

  /* --- open / close ------------------------------------------------------- */
  function open(trigger) {
    var o = root();
    if (!o || o.classList.contains('is-open')) return;

    lastTrigger = trigger || null;
    panel = panel || o.querySelector('[data-ambury-search-panel]');
    input = input || o.querySelector('[data-ambury-search-input]');
    emptyState = emptyState || o.querySelector('[data-ambury-search-empty]');

    setScrollbarWidth();
    o.hidden = false;
    o.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    /* next frame so the transition has a starting state to animate from */
    window.requestAnimationFrame(function () {
      o.classList.add('is-active');
    });

    syncEmptyState();

    if (input) {
      input.focus({ preventScroll: true });
      if (typeof window.trapFocus === 'function') window.trapFocus(panel || o, input);
    }
  }

  function close() {
    var o = root();
    if (!o || !o.classList.contains('is-open')) return;

    o.classList.remove('is-active');
    document.body.classList.remove('overflow-hidden');

    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(lastTrigger || undefined);
    } else if (lastTrigger) {
      lastTrigger.focus();
    }

    var finish = function () {
      o.classList.remove('is-open');
      o.hidden = true;
    };

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || (window.Shopify && window.Shopify.designMode)) {
      finish();
    } else {
      window.setTimeout(finish, 200);
    }
  }

  /* --- wiring ------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var trigger = event.target.closest('[data-ambury-search-open]');
    if (trigger) {
      /* Middle-click, ctrl-click and friends should still open /search. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (!root()) return;
      event.preventDefault();
      loadSearchAssets();
      open(trigger);
      return;
    }

    if (event.target.closest('[data-ambury-search-close]')) {
      event.preventDefault();
      close();
    }
  });

  /* Warm the scripts on intent, before the click completes. */
  ['pointerenter', 'focusin', 'touchstart'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        if (event.target.closest && event.target.closest('[data-ambury-search-open]')) loadSearchAssets();
      },
      { passive: true, capture: true }
    );
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var o = root();
    if (o && o.classList.contains('is-open')) close();
  });

  document.addEventListener('input', function (event) {
    if (event.target && event.target.hasAttribute && event.target.hasAttribute('data-ambury-search-input')) {
      syncEmptyState();
    }
  });

  window.addEventListener('resize', setScrollbarWidth, { passive: true });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('DOMContentLoaded', function () {
      var o = root();
      if (o) o.classList.add('ambury-no-motion');
    });
  }
})();
